import { reactive } from 'vue';
import { 
  faCheckCircle, 
  faExclamationTriangle, 
  faInfoCircle, 
  faTimesCircle 
} from '@fortawesome/free-solid-svg-icons';

class ToastService {
  constructor() {
    this.toasts = reactive([]);
    this.nextId = 1;
    this.maxToasts = 4;
    this.defaultDuration = 5000; // 5 seconds
  }

  show(message, type = 'info', options = {}) {
    const toast = {
      id: this.nextId++,
      message,
      type,
      icon: this.getIcon(type),
      duration: options.duration || this.defaultDuration,
      isPaused: false,
      timeRemaining: options.duration || this.defaultDuration,
      timer: null
    };

    // Remove oldest toast if we're at the limit
    if (this.toasts.length >= this.maxToasts) {
      this.remove(this.toasts[0].id);
    }

    this.toasts.push(toast);
    this.startTimer(toast);
    
    return toast.id;
  }

  success(message, options = {}) {
    return this.show(message, 'success', options);
  }

  error(message, options = {}) {
    return this.show(message, 'error', options);
  }

  warning(message, options = {}) {
    return this.show(message, 'warning', options);
  }

  info(message, options = {}) {
    return this.show(message, 'info', options);
  }

  remove(id) {
    const index = this.toasts.findIndex(toast => toast.id === id);
    if (index > -1) {
      const toast = this.toasts[index];
      if (toast.timer) {
        clearTimeout(toast.timer);
      }
      this.toasts.splice(index, 1);
    }
  }

  pause(id) {
    const toast = this.toasts.find(t => t.id === id);
    if (toast && !toast.isPaused) {
      toast.isPaused = true;
      if (toast.timer) {
        clearTimeout(toast.timer);
        toast.timer = null;
      }
    }
  }

  resume(id) {
    const toast = this.toasts.find(t => t.id === id);
    if (toast && toast.isPaused) {
      toast.isPaused = false;
      this.startTimer(toast);
    }
  }

  clear() {
    this.toasts.forEach(toast => {
      if (toast.timer) {
        clearTimeout(toast.timer);
      }
    });
    this.toasts.splice(0);
  }

  startTimer(toast) {
    if (toast.timer) {
      clearTimeout(toast.timer);
    }
    
    toast.timer = setTimeout(() => {
      this.remove(toast.id);
    }, toast.timeRemaining);
  }

  getIcon(type) {
    const icons = {
      success: faCheckCircle,
      error: faTimesCircle,
      warning: faExclamationTriangle,
      info: faInfoCircle
    };
    return icons[type] || icons.info;
  }

  getBulmaClass(type) {
    const classes = {
      success: 'is-success',
      error: 'is-danger',
      warning: 'is-warning',
      info: 'is-info'
    };
    return classes[type] || classes.info;
  }

  // Handle API errors automatically
  handleApiError(error) {
    try {
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     error.message || 
                     'An unexpected error occurred';
      
      this.error(message);
      console.error('API Error:', error);
    } catch (fallbackError) {
      console.error('Toast service error:', fallbackError);
      console.error('Original API error:', error);
    }
  }
}

export const toastService = new ToastService();